import { makeObservable, observable, runInAction, action } from 'mobx';
import { ApplicationSdk } from '../../../core/sdk';
import BaseAdminStore, { AdminApiResponse } from '../../../shared/stores/baseAdminStore';
import { setPrimaryColor } from '../../../utils/helper';

/**
 * The Design Management store manages the design configuration settings for the admin panel.
 */
class DesignManagementStore extends BaseAdminStore {
    public logoUrl: string | null = null;
    public primaryColor: string = '#1976d2'; // Default Material UI Blue
    public logoFile: File | null = null;
    public isUploadingLogo: boolean = false;
    public imageBlob: string | null = null; // For UploadImageDialog compatibility

    constructor(sdk: ApplicationSdk) {
        super(sdk);
        makeObservable(this, {
            logoUrl: observable,
            primaryColor: observable,
            logoFile: observable,
            isUploadingLogo: observable,
            imageBlob: observable,
            setLogoUrl: action,
            setPrimaryColor: action,
            setLogoFile: action,
            setIsUploadingLogo: action,
            setImage: action,
        });
    }

    // Implement required abstract methods
    protected getApiEndpoint(): string {
        return 'system';
    }

    protected getApiPath(): string {
        return 'get_design_settings';
    }

    protected buildApiParams(): any {
        return {};
    }

    protected processApiResponse(response: AdminApiResponse): any {
        runInAction(() => {
            if (response.status === 200) {
                const { logoUrl, primaryColor } = response.json || {};
                this.logoUrl = logoUrl || null;
                this.primaryColor = primaryColor || '#1976d2';
            }
        });
        return response.json;
    }

    public setLogoUrl(logoUrl: string | null): void {
        this.logoUrl = logoUrl;
    }

    public setPrimaryColor(color: string): void {
        this.primaryColor = color;
    }

    public setLogoFile(file: File | null): void {
        this.logoFile = file;
    }

    public setIsUploadingLogo(uploading: boolean): void {
        this.isUploadingLogo = uploading;
    }

    public setImage(image: File | Blob | string): void {
        if (image instanceof File || image instanceof Blob) {
            this.logoFile =
                image instanceof File
                    ? image
                    : new File([image], 'logo.jpg', { type: 'image/jpeg' });
            this.imageBlob = URL.createObjectURL(image);
        } else if (typeof image === 'string') {
            this.imageBlob = image;
        }
    }

    public async uploadLogo(): Promise<boolean> {
        if (!this.logoFile) {
            console.error('Bitte wählen Sie ein Logo aus.');
            return false;
        }

        this.setIsUploadingLogo(true);
        try {
            const response = await this.sdk.uploadImage('system', 'upload_logo', this.logoFile);
            if (response && response.json?.success && response.json?.data?.url) {
                runInAction(() => {
                    this.logoUrl = response.json.data.url;
                    this.logoFile = null;
                    this.imageBlob = null; // Clear the blob after successful upload
                });
                return true;
            } else {
                console.error('Logo konnte nicht hochgeladen werden.');
                return false;
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            return false;
        } finally {
            this.setIsUploadingLogo(false);
        }
    }

    public async savePrimaryColor(): Promise<boolean> {
        try {
            const response: AdminApiResponse = await this.makeApiCall(
                'system',
                'save_primary_color',
                {
                    color: this.primaryColor,
                }
            );

            if (response.status === 200 && response.json?.success) {
                setPrimaryColor(this.primaryColor);
                return true;
            } else {
                console.error('Primärfarbe konnte nicht gespeichert werden.');
                return false;
            }
        } catch (error) {
            console.error('Error saving primary color:', error);
            return false;
        }
    }

    public async resetLogo(): Promise<boolean> {
        try {
            const response: AdminApiResponse = await this.makeApiCall('system', 'reset_logo');
            if (response.status === 200 && response.json?.success) {
                runInAction(() => {
                    this.logoUrl = null;
                    this.imageBlob = null; // Clear the blob as well
                });
                return true;
            } else {
                console.error('Logo konnte nicht zurückgesetzt werden.');
                return false;
            }
        } catch (error) {
            console.error('Error resetting logo:', error);
            return false;
        }
    }
}

export default DesignManagementStore;
